/*
 * @Author: Yun 
 * @Date: 2025-09-24 13:57:24 
 * @Last Modified by:   Yun 
 * @Last Modified time: 2025-09-24 13:57:24 
 */

export interface LoaderOptions {
  resources?: ResourceItem[];
  debugMode?: boolean;
  onBeforeLoad?: () => void;
  onComplete?: () => void;
  onElementLoaded?: (resource: ResourceItem, element: HTMLImageElement) => void;
  onUpdate?: (percentage: number) => void;
  onError?: (resource: ResourceItem, error: Error) => void;
}

export interface ResourceItem {
  id: string;
  url: string;
  size: number;
  priority: 'high' | 'medium' | 'low';
  type?: 'image' | 'font' | 'video';
  description?: string;
}

interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
}

class ResourceLoader {
  private options: Required<LoaderOptions>;
  private currentSegment = 0;
  private bytesLoaded = 0;
  private bytesTotal = 0;
  private resourceGroups: ResourceItem[][] = [];
  private deviceInfo: DeviceInfo;
  private isInitialized = false; // 防重复初始化标志

  constructor(customOptions: LoaderOptions = {}) {
    const defaults: Required<LoaderOptions> = {
      resources: [],
      debugMode: false,
      onBeforeLoad: () => {},
      onComplete: () => {},
      onElementLoaded: () => {},
      onUpdate: () => {},
      onError: () => {},
    };

    this.options = { ...defaults, ...customOptions };
    this.deviceInfo = this.detectDevice();
  }

  /**
   * 检测设备类型
   */
  private detectDevice(): DeviceInfo {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    const isTablet = /iPad|Android|(?=.*\bWindows\b)(?=.*\bARM\b)/i.test(userAgent);
    
    const isMobile = /android.+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|meego.+mobile|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(userAgent) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(di|rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(userAgent.substr(0, 4));

    return { isMobile, isTablet };
  }

  /**
   * 输出调试信息
   */
  private log(message: any): void {
    if (this.options.debugMode && console) {
      // console.log('[ResourceLoader]', message);
    }
  }

  /**
   * 检查媒体格式支持
   */
  private checkMediaSupport() {
    const video = document.createElement('video');
    const audio = document.createElement('audio');

    const videoSupport = {
      mp4: video.canPlayType('video/mp4; codecs="avc1.42E01E"').replace(/^no$/, ''),
      webm: video.canPlayType('video/webm; codecs="vp8, vorbis"').replace(/^no$/, ''),
      ogg: video.canPlayType('video/ogg; codecs="theora"').replace(/^no$/, ''),
    };

    const audioSupport = {
      mp3: audio.canPlayType('audio/mpeg;').replace(/^no$/, ''),
      ogg: audio.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, ''),
      wav: audio.canPlayType('audio/wav; codecs="1"').replace(/^no$/, ''),
      m4a: (audio.canPlayType('audio/x-m4a;') || audio.canPlayType('audio/aac;')).replace(/^no$/, ''),
    };

    return { video: videoSupport, audio: audioSupport };
  }

  /**
   * 更新进度百分比
   */
  private updatePercentage(): void {
    const percentage = this.bytesTotal > 0 ? Math.round((this.bytesLoaded / this.bytesTotal) * 100) : 0;
    
    this.log(`进度: ${percentage}% (${this.bytesLoaded}/${this.bytesTotal} bytes)`);
    
    this.options.onUpdate(percentage);

    // 检查是否需要加载下一组资源
    if (!this.resourceGroups[this.currentSegment]?.length) {
      this.currentSegment++;
      
      if (this.resourceGroups[this.currentSegment]) {
        this.startLoading();
      } else {
        this.log('所有资源加载完成');
        this.options.onComplete();
      }
    }
  }

  /**
   * 加载字体资源
   */
  private loadFont(resource: ResourceItem): Promise<void> {
    return new Promise((resolve) => {
      // 提取字体文件名作为字体家族名
      const fontFamily = resource.id.replace('font-', '').replace(/-/g, ' ');
      
      // 使用 FontFace API 加载字体
      const fontFace = new FontFace(fontFamily, `url(${resource.url})`);
      
      fontFace.load()
        .then((loadedFont) => {
          // 将字体添加到文档中
          (document.fonts as any).add(loadedFont);
          this.log(`字体加载完成: ${resource.url}`);
          this.bytesLoaded += resource.size;
          
          // 从当前组中移除已加载的资源
          const currentGroup = this.resourceGroups[this.currentSegment];
          const index = currentGroup.findIndex(r => r.id === resource.id);
          if (index !== -1) {
            currentGroup.splice(index, 1);
          }
          
          this.updatePercentage();
          resolve();
        })
        .catch((error) => {
          this.log(`字体加载失败: ${resource.url}, ${error}`);
          this.options.onError(resource, error);
          
          // 即使失败也要继续
          this.bytesLoaded += resource.size;
          const currentGroup = this.resourceGroups[this.currentSegment];
          const index = currentGroup.findIndex(r => r.id === resource.id);
          if (index !== -1) {
            currentGroup.splice(index, 1);
          }
          this.updatePercentage();
          resolve();
        });
    });
  }

  /**
   * 加载图片资源
   */
  private loadImage(resource: ResourceItem): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.log(`图片加载完成: ${resource.url}`);
        this.bytesLoaded += resource.size;
        this.options.onElementLoaded(resource, img);
        
        // 从当前组中移除已加载的资源
        const currentGroup = this.resourceGroups[this.currentSegment];
        const index = currentGroup.findIndex(r => r.id === resource.id);
        if (index !== -1) {
          currentGroup.splice(index, 1);
        }
        
        this.updatePercentage();
        resolve(img);
      };

      img.onerror = () => {
        const error = new Error(`图片加载失败: ${resource.url}`);
        this.log(error.message);
        this.options.onError(resource, error);
        
        // 即使失败也要继续，避免阻塞整个加载流程
        this.bytesLoaded += resource.size;
        const currentGroup = this.resourceGroups[this.currentSegment];
        const index = currentGroup.findIndex(r => r.id === resource.id);
        if (index !== -1) {
          currentGroup.splice(index, 1);
        }
        this.updatePercentage();
        resolve(img);
      };

      img.src = resource.url;
    });
  }

  /**
   * 预加载视频元数据（不加载整个视频文件）
   */
  private loadVideo(resource: ResourceItem): Promise<void> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata'; // 只加载元数据，不加载整个视频
      video.muted = true; // 静音以避免自动播放限制
      
      const onMetadataLoaded = () => {
        this.log(`视频元数据加载完成: ${resource.url}`);
        this.bytesLoaded += resource.size;
        
        // 从当前组中移除已加载的资源
        const currentGroup = this.resourceGroups[this.currentSegment];
        if (currentGroup) {
          const index = currentGroup.findIndex(r => r.id === resource.id);
          if (index !== -1) {
            currentGroup.splice(index, 1);
          }
        }
        
        cleanup();
        this.updatePercentage();
        resolve();
      };

      const onError = () => {
        const error = new Error(`视频元数据加载失败: ${resource.url}`);
        this.log(error.message);
        this.options.onError(resource, error);
        
        // 即使失败也要继续
        this.bytesLoaded += resource.size;
        const currentGroup = this.resourceGroups[this.currentSegment];
        if (currentGroup) {
          const index = currentGroup.findIndex(r => r.id === resource.id);
          if (index !== -1) {
            currentGroup.splice(index, 1);
          }
        }
        
        cleanup();
        this.updatePercentage();
        resolve();
      };

      const cleanup = () => {
        video.removeEventListener('loadedmetadata', onMetadataLoaded);
        video.removeEventListener('error', onError);
        video.src = '';
        video.load();
      };

      // 设置超时，避免视频加载卡住
      const timeout = setTimeout(() => {
        this.log(`视频元数据加载超时: ${resource.url}`);
        onError();
      }, 10000); // 10秒超时

      video.addEventListener('loadedmetadata', () => {
        clearTimeout(timeout);
        onMetadataLoaded();
      });
      
      video.addEventListener('error', () => {
        clearTimeout(timeout);
        onError();
      });

      video.src = resource.url;
    });
  }


  /**
   * 开始加载当前组的资源
   */
  private startLoading(): void {
    const currentGroup = [...this.resourceGroups[this.currentSegment]];
    
    this.log(`开始加载第${this.currentSegment + 1}组资源，共${currentGroup.length}个资源`);

    // 并行加载所有资源
    currentGroup.forEach(resource => {
      this.log(`正在预加载: ${resource.url}`);
      
      // 根据资源类型选择加载方法
      if (resource.type === 'font') {
        this.loadFont(resource);
      } else if (resource.type === 'video') {
        this.loadVideo(resource);
      } else {
        // 默认作为图片加载
        this.loadImage(resource);
      }
    });
  }

  /**
   * 处理资源数据
   */
  private arrangeData(resources: ResourceItem[]): void {
    // 按优先级分组资源
    const highPriority = resources.filter(r => r.priority === 'high');
    const mediumPriority = resources.filter(r => r.priority === 'medium');
    const lowPriority = resources.filter(r => r.priority === 'low');

    // 清空现有分组
    this.resourceGroups = [];
    this.bytesTotal = 0;

    // 添加高优先级组
    if (highPriority.length > 0) {
      this.resourceGroups.push([...highPriority]);
      this.bytesTotal += highPriority.reduce((sum, r) => sum + r.size, 0);
    }

    // 添加中优先级组
    if (mediumPriority.length > 0) {
      this.resourceGroups.push([...mediumPriority]);
      this.bytesTotal += mediumPriority.reduce((sum, r) => sum + r.size, 0);
    }

    // 添加低优先级组
    if (lowPriority.length > 0) {
      this.resourceGroups.push([...lowPriority]);
      this.bytesTotal += lowPriority.reduce((sum, r) => sum + r.size, 0);
    }

    this.log(`总共${this.resourceGroups.length}组资源，预估大小: ${this.bytesTotal} KB`);
  }

  /**
   * 初始化并开始加载
   */
  public async init(): Promise<void> {
    // 防止重复初始化
    if (this.isInitialized) {
      this.log('资源加载器已经初始化，跳过重复初始化');
      return;
    }
    
    this.isInitialized = true;
    this.log('资源加载器初始化');
    
    this.options.onBeforeLoad();

    if (!this.options.resources || this.options.resources.length === 0) {
      this.log('没有需要预加载的资源');
      this.options.onComplete();
      return;
    }

    // 处理资源数据
    this.arrangeData(this.options.resources);

    // 开始加载
    if (this.resourceGroups.length > 0) {
      this.startLoading();
    } else {
      this.options.onComplete();
    }
  }
}

export default ResourceLoader;